import { isCommonResponseSuccessfulV2 } from 'src/mentor/helpers/utilityFunctions'
import { getMPDataWithParams, postMPDataWithoutParams } from 'src/mentor/services'
import { getCPDataWithParams, postCPDataWithoutParams } from 'src/clinic/services'
import {
  getSPDataWithParams,
  postSPDataWithoutParams,
} from 'src/supportPortal/services/services'

function getResponseMessage(response, fallbackMessage) {
  return (
    response?.data?.message ||
    response?.response?.data?.message ||
    response?.message ||
    fallbackMessage
  )
}

export async function getMentorPortalVoiceOptions({ bookedEventId, clinicBrandId, clinicId } = {}) {
  return getPortalVoiceOptions({
    actorRole: 'mentor',
    bookedEventId,
    clinicBrandId,
    clinicId,
  })
}

function getActorConfig(actorRole = 'mentor') {
  if (actorRole === 'clinic-assistant') {
    return {
      getWithParams: getCPDataWithParams,
      postWithoutParams: postCPDataWithoutParams,
      basePath: '/api/secure/clinic-assistant/portal-voice',
    }
  }

  if (actorRole === 'support-agent') {
    return {
      getWithParams: getSPDataWithParams,
      postWithoutParams: postSPDataWithoutParams,
      basePath: '/api/secure/cc/support/portal-voice',
    }
  }

  return {
    getWithParams: getMPDataWithParams,
    postWithoutParams: postMPDataWithoutParams,
    basePath: '/api/secure/mentor/portal-voice',
  }
}

export async function getPortalVoiceOptions({
  actorRole = 'mentor',
  bookedEventId,
  clinicBrandId,
  clinicId,
  practitionerId,
  patientId,
  targetRole,
  eventType,
  eventTypesCsv,
} = {}) {
  const hasClinicContext = !!(clinicBrandId && clinicId)
  const hasSupportTarget = actorRole === 'support-agent' && !!(patientId || practitionerId)

  if (!bookedEventId && !hasClinicContext && !hasSupportTarget) {
    return {
      ok: false,
      message:
        actorRole === 'support-agent'
          ? 'Patient or practitioner context is required.'
          : 'Booked event id or clinic context is required.',
      routes: [],
    }
  }

  const actorConfig = getActorConfig(actorRole)
  const response = await actorConfig.getWithParams(`${actorConfig.basePath}/options`, {
    bookedEventId,
    clinicBrandId,
    clinicId,
    practitionerId,
    patientId,
    targetRole,
    eventType,
    eventTypesCsv,
  })

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return {
      ok: true,
      routes: response?.data?.data?.routes || [],
      voiceConfigured: !!response?.data?.data?.twilioConfigured,
      bookedEventId: response?.data?.data?.bookedEventId || bookedEventId,
      clinicBrandId: response?.data?.data?.clinicBrandId || clinicBrandId,
      clinicId: response?.data?.data?.clinicId || clinicId,
      practitionerId: response?.data?.data?.practitionerId || practitionerId,
      patientId: response?.data?.data?.patientId || patientId,
      targetRole: response?.data?.data?.targetRole || targetRole,
      rawResponse: response,
    }
  }

  return {
    ok: false,
    message: getResponseMessage(response, 'Unable to load portal call routes.'),
    routes: [],
    rawResponse: response,
  }
}

export async function createMentorPortalVoiceBootstrap({
  bookedEventId,
  clinicBrandId,
  clinicId,
  destinationType,
  uiSurface,
}) {
  return createPortalVoiceBootstrap({
    actorRole: 'mentor',
    bookedEventId,
    clinicBrandId,
    clinicId,
    destinationType,
    uiSurface,
  })
}

export async function createPortalVoiceBootstrap({
  actorRole = 'mentor',
  bookedEventId,
  clinicBrandId,
  clinicId,
  practitionerId,
  patientId,
  targetRole,
  eventType,
  eventTypesCsv,
  destinationType,
  uiSurface,
}) {
  const hasClinicContext = !!(clinicBrandId && clinicId)
  const hasSupportTarget = actorRole === 'support-agent' && !!(patientId || practitionerId)

  if ((!bookedEventId && !hasClinicContext && !hasSupportTarget) || !destinationType) {
    return {
      ok: false,
      message:
        actorRole === 'support-agent'
          ? 'Destination type and patient or practitioner context are required.'
          : 'Destination type and either booked event id or clinic context are required.',
    }
  }

  const actorConfig = getActorConfig(actorRole)
  const response = await actorConfig.postWithoutParams(`${actorConfig.basePath}/bootstrap`, {
    bookedEventId,
    clinicBrandId,
    clinicId,
    practitionerId,
    patientId,
    targetRole,
    eventType,
    eventTypesCsv,
    destinationType,
    uiSurface,
  })

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return {
      ok: true,
      providerType: response?.data?.data?.providerType || response?.data?.data?.route?.providerType,
      accessToken: response?.data?.data?.accessToken,
      requestToken: response?.data?.data?.requestToken,
      providerTransportToken:
        response?.data?.data?.providerTransportToken ||
        response?.data?.data?.providerSession?.providerTransportToken ||
        '',
      renewalToken: response?.data?.data?.renewalToken,
      callSessionId: response?.data?.data?.callSessionId,
      identity: response?.data?.data?.identity,
      expiresInSeconds: response?.data?.data?.expiresInSeconds,
      route: response?.data?.data?.route || {},
      providerSession: response?.data?.data?.providerSession || {},
      rawResponse: response,
    }
  }

  return {
    ok: false,
    message: getResponseMessage(response, 'Unable to prepare the portal voice call.'),
    rawResponse: response,
  }
}

export async function renewMentorPortalVoiceCall({ interactionId, renewalToken }) {
  return renewPortalVoiceCall({
    actorRole: 'mentor',
    interactionId,
    renewalToken,
  })
}

export async function renewPortalVoiceCall({
  actorRole = 'mentor',
  interactionId,
  renewalToken,
}) {
  if (!interactionId || !renewalToken) {
    return {
      ok: false,
      message: 'interactionId and renewalToken are required.',
    }
  }

  const actorConfig = getActorConfig(actorRole)
  const response = await actorConfig.postWithoutParams(`${actorConfig.basePath}/renew`, {
    interactionId,
    renewalToken,
  })

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return {
      ok: true,
      interactionId: response?.data?.data?.interactionId || interactionId,
      callExpiresAt: response?.data?.data?.callExpiresAt,
      callTimeLimitSeconds: response?.data?.data?.callTimeLimitSeconds,
      maxCallDurationSeconds: response?.data?.data?.maxCallDurationSeconds,
      renewalIncrementSeconds: response?.data?.data?.renewalIncrementSeconds,
      renewalAllowed: !!response?.data?.data?.renewalAllowed,
      renewalCount: response?.data?.data?.renewalCount || 0,
      renewalToken: response?.data?.data?.renewalToken,
      remainingSeconds: response?.data?.data?.remainingSeconds,
      rawResponse: response,
    }
  }

  return {
    ok: false,
    message: getResponseMessage(response, 'Unable to extend the portal voice call.'),
    rawResponse: response,
  }
}
